import React, { JSX } from 'react';
import { Column, useTable } from 'react-table';
import { CloseIcon } from '@chakra-ui/icons';
import { Box, Flex, Table as ChakraTable, Tbody, Td, Text, Th, Thead, Tr, useTheme } from '@chakra-ui/react';
import { AxiosError } from 'axios';
import { useTranslations } from 'contexts/LocalizationContext';

import { HttpException } from '../../models/httpException';
import { ErrorMessages } from '../ErrorMessages';
import { FullPageLoader } from '../Loader';
import { Pagination } from './Pagination/Pagination';
import { Searchbox } from './Searchbox/Searchbox';

type TableProps<DataType extends object> = {
  data: DataType[] | undefined;
  columns: Column<DataType>[];
  isLoading: boolean;
  onRowClicked?: (id: string) => void;
  customFilter?: React.ReactNode[] | React.ReactNode;
  children?: React.ReactNode[] | React.ReactNode;
} & (
  | { searchEnabled?: false }
  | {
      searchEnabled: true;
      searchText: string;
      onSearchTextChanged: (text: string) => void;
      searchboxPlaceholder?: string;
    }
) &
  (
    | { paginationEnabled?: false }
    | {
        paginationEnabled: true;
        page: number;
        onPageChanged: (newPage: number) => void;
        totalPages: number | undefined;
      }
  ) & { errors?: (AxiosError<HttpException> | null)[] | AxiosError<HttpException> | null };

export const Table = <DataType extends object>(props: TableProps<DataType>): JSX.Element => {
  const { columns, data, isLoading } = props;
  const { headerGroups, rows, prepareRow } = useTable({ columns, data: data || [] });
  const { colors } = useTheme();
  const translations = useTranslations();

  return (
    <>
      {props.searchEnabled || props.children ? (
        <Flex m={1} alignItems="center" flexWrap="wrap" gap={2} w="100%">
          {props.searchEnabled && (
            <Flex flex="1 1 300px" minW="200px" maxW="500px">
              {props.customFilter || (
                <Searchbox
                  value={props.searchText}
                  onChange={props.onSearchTextChanged}
                  placeholder={props.searchboxPlaceholder ?? ''}
                />
              )}
            </Flex>
          )}
          {props.children ? (
            <Flex
              mr={1}
              flex="0 1 auto"
              ml={{ base: 0, md: 'auto' }}
              mt={{ base: 1, md: 0 }}
              alignItems="center"
              minW="fit-content"
            >
              {props.children}
            </Flex>
          ) : null}
        </Flex>
      ) : null}
      <Box overflowX="auto">
        <FullPageLoader show={isLoading}>
          <ChakraTable p={1} {...(!rows.length && { border: `1px solid ${colors.gray[100]}` })}>
            <Thead>
              {headerGroups.map((headerGroup) => (
                <Tr key={`headerGroup_${headerGroup.id}`}>
                  {headerGroup.headers.map((column) => (
                    <Th key={`header_${column.id}`}>{column.render('Header') as any}</Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {props.errors ? (
                <Tr>
                  <Td colSpan={columns.length} p={10}>
                    <ErrorMessages errors={props.errors} />
                  </Td>
                </Tr>
              ) : (
                <>
                  {rows.length ? (
                    <>
                      {rows.map((row) => {
                        prepareRow(row);
                        return (
                          <Tr key={`row_${row.id}`}>
                            {row.cells.map((cell) => (
                              <Td key={`cell_${cell.column.id}`}>{cell.render('Cell') as any}</Td>
                            ))}
                          </Tr>
                        );
                      })}
                    </>
                  ) : (
                    <Tr>
                      <Td colSpan={columns.length} p={10}>
                        <CloseIcon margin="auto" fontSize="20px" mb={2} />
                        <Text textAlign="center" fontSize="18px">
                          {translations('no_data')}
                        </Text>
                      </Td>
                    </Tr>
                  )}
                </>
              )}
            </Tbody>
          </ChakraTable>
          {props.paginationEnabled && props?.totalPages && props.totalPages > 1 ? (
            <Flex w="100%" justifyContent="center">
              <Pagination page={props.page} totalPages={props.totalPages} onPageChanged={props.onPageChanged} />
            </Flex>
          ) : null}
        </FullPageLoader>
      </Box>
    </>
  );
};
